import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as productService from "../services/productService";
import { useMemo } from "react";
import * as jobService from "../services/jobService";

export const useJobProducts = () => {
  const [showingProductFormForJob, setShowingProductFormForJob] = useState(null);
  const [selectedJobForProduct, setSelectedJobForProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);

  // Fetch products for product form
  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ["products"],
    queryFn: productService.getProducts,
  });

  // Ensure products is always an array and handle the API response structure
  const products = useMemo(() => {

    if (!productsData) return [];
    // Handle different possible response structures
    if (Array.isArray(productsData)) return productsData;
    if (productsData.products && Array.isArray(productsData.products)) return productsData.products;
    if (productsData.content && Array.isArray(productsData.content)) return productsData.content;
    console.warn("Unexpected products data structure:", productsData);
    return [];
  }, [productsData]);

  const openProductForm = (job) => {
    setShowingProductFormForJob(job.id);
  };

  const closeProductForm = () => {
    setShowingProductFormForJob(null);
    setShowProductForm(false);
    setSelectedJobForProduct(null);
  };

  const handleBarcodeProduct = async (jobId, productData, addProductToJob, selectedJobForDetails, setSelectedJobForDetails, queryClient, searchTerm, page, pageSize, statusFilter) => {
    try {
      // First, try to find an existing product with the same name
      let productToUse = null;
      try {
        const existingProducts = await productService.searchProductsByName(productData.name);

        if (existingProducts && existingProducts.length > 0) {
          // Find the best match - prefer exact name and price match
          const exactMatch = existingProducts.find(product =>
            product.name.toLowerCase() === productData.name.toLowerCase() &&
            Math.abs((product.unitPrice || 0) - (productData.unitPrice || productData.price || 0)) < 0.01
          );

          if (exactMatch) {
            productToUse = exactMatch;
          } else {
            // Look for case-insensitive name match with similar price (within 10% tolerance)
            const nameMatch = existingProducts.find(product => {
              const nameMatches = product.name.toLowerCase() === productData.name.toLowerCase();
              const priceDiff = Math.abs((product.unitPrice || 0) - (productData.unitPrice || productData.price || 0));
              const priceTolerance = Math.max((productData.unitPrice || productData.price || 0) * 0.1, 0.01);
              return nameMatches && priceDiff <= priceTolerance;
            });

            if (nameMatch) {
              productToUse = nameMatch;
            } else {
              // Use the first product with the same name if no price match
              productToUse = existingProducts[0];
            }
          }
        }
      } catch (searchError) {
        console.error("Failed to search for existing products:", searchError);
        console.warn("Will create new product due to search failure");
      }

      // If no existing product found, create a new one
      if (!productToUse) {
        const newProductData = {
          name: productData.name,
          description: productData.description || `Product from barcode scan: ${productData.name}`,
          unitPrice: productData.unitPrice || productData.price || 0,
          category: productData.category || "Barcode Scan",
          supplier: productData.supplier || "Unknown"
        };

        productToUse = await productService.createProduct(newProductData);

        // Invalidate products query to refresh the products list
        queryClient.invalidateQueries({ queryKey: ["products"] });
      }

      // Now add the product to the job using the found or created product
      const productDto = {
        productId: productToUse.id,
        quantity: productData.quantity || 1,
        unitPrice: productData.unitPrice || productData.price || 0
      };

      // Create sale and optimistically append to details view
      const sale = await addProductToJob.mutateAsync({
        jobId: jobId,
        material: productDto
      });
      if (selectedJobForDetails && selectedJobForDetails.id === jobId && sale) {
        const currentSales = Array.isArray(selectedJobForDetails.sales) ? selectedJobForDetails.sales : [];
        setSelectedJobForDetails({ ...selectedJobForDetails, sales: [...currentSales, sale] });
      }

      // Avoid invalidating the entire jobs list to preserve context
      // The selected job detail is refreshed below when applicable

      // Update the selected job details if it's currently open
      if (selectedJobForDetails && selectedJobForDetails.id === jobId) {
        try {
          // Refetch the specific job to get updated data
          const updatedJob = await jobService.getJob(jobId);
          if (updatedJob) {
            setSelectedJobForDetails(updatedJob);
          }
        } catch (error) {
          console.error("Failed to refetch job:", error);
          // Don't let this error break the flow
        }
      }

      // Product added successfully (non-blocking)


      // Close the product form after successful barcode product addition
      closeProductForm();
    } catch (error) {
      console.error("Failed to add barcode product:", error);
    }
  };

  const handleAddProduct = (productData, addProductToJob, selectedJobForDetails, setSelectedJobForDetails) => {
    if (showingProductFormForJob) {
      // Check if this is a barcode scanned product
      if (productData.source === "Barcode Scan") {
        handleBarcodeProduct(showingProductFormForJob, productData, addProductToJob, selectedJobForDetails, setSelectedJobForDetails);
      } else {
        // Handle regular product addition and optimistically update


        addProductToJob.mutateAsync({
          jobId: showingProductFormForJob,
          material: productData,
        }).then((sale) => {
          if (selectedJobForDetails && selectedJobForDetails.id === showingProductFormForJob && sale) {
            const currentSales = Array.isArray(selectedJobForDetails.sales) ? selectedJobForDetails.sales : [];
            setSelectedJobForDetails({ ...selectedJobForDetails, sales: [...currentSales, sale] });
          }
        }).catch((err) => console.error("Failed to add product:", err));
      }
      closeProductForm();
    }
  };

  return {
    showingProductFormForJob,
    selectedJobForProduct,
    setSelectedJobForProduct,
    showProductForm,
    setShowProductForm,
    products,
    productsLoading,
    productsError,
    openProductForm,
    closeProductForm,
    handleAddProduct,
    handleBarcodeProduct,
    // Legacy aliases for backwards compatibility
    showingMaterialFormForJob: showingProductFormForJob,
    selectedJobForMaterial: selectedJobForProduct,
    setSelectedJobForMaterial: setSelectedJobForProduct,
    showMaterialForm: showProductForm,
    setShowMaterialForm: setShowProductForm,
    openMaterialForm: openProductForm,
    closeMaterialForm: closeProductForm,
    handleAddMaterial: handleAddProduct,
    handleBarcodeMaterial: handleBarcodeProduct,
  };
};

// Legacy alias for backwards compatibility
export const useJobMaterials = useJobProducts;
